import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, Check, Clock, Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AdminOrders() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/");
      return;
    }
    fetchOrders();
  }, [user, token, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, payment_status: status } : o
      ));
      toast.success(`Order marked as ${status}`);
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredOrders = orders.filter(order => {
    const matchesFilter = filter === "all" || order.payment_status === filter;
    const matchesSearch = order.id.toLowerCase().includes(search.toLowerCase()) ||
      order.shipping_address.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="admin-orders">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-[#888] hover:text-[#39FF14] transition-colors mb-4"
          >
            <ArrowLeft size={18} />
            <span className="label-text">Back to Dashboard</span>
          </Link>
          <h1 className="text-4xl heading text-[#EDEDED]">Orders</h1>
          <p className="text-[#888] mt-1">{orders.length} total orders</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#888]" size={20} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by order ID or address..."
              className="w-full gcsr-input pl-12"
              data-testid="search-orders"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 bg-transparent border-[#333] text-[#EDEDED]" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#333]">
              <SelectItem value="all" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">All Orders</SelectItem>
              <SelectItem value="pending" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">Pending</SelectItem>
              <SelectItem value="confirmed" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">Confirmed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded-lg"></div>
            ))}
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-16 bg-[#0A0A0A] border border-[#222]">
            <ShoppingCart size={48} className="mx-auto text-[#333] mb-4" />
            <p className="text-[#888]">
              {search || filter !== "all" ? "No orders match your criteria" : "No orders yet"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                className="bg-[#0A0A0A] border border-[#222] p-6"
                data-testid={`order-${order.id}`}
              >
                {/* Order Header */}
                <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-[#222]">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#888] text-sm">Order ID:</span>
                      <span className="text-[#EDEDED] font-mono">{order.id.slice(0, 8)}</span>
                      <span className={`status-badge ${order.payment_status}`}>
                        {order.payment_status === "confirmed" ? (
                          <><Check size={12} className="mr-1" /> Confirmed</>
                        ) : (
                          <><Clock size={12} className="mr-1" /> Pending</>
                        )}
                      </span>
                    </div>
                    <p className="text-[#888] text-sm">{formatDate(order.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#39FF14] text-2xl heading">${order.total.toFixed(2)}</p>
                    <p className="text-[#888] text-sm capitalize">via {order.payment_method}</p>
                  </div>
                </div>

                {/* Order Items */}
                <div className="py-4 border-b border-[#222]">
                  <h4 className="text-[#888] label-text text-sm mb-3">Items ({order.items.length})</h4>
                  <div className="flex flex-wrap gap-4">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-12 h-12 object-cover"
                        />
                        <div>
                          <p className="text-[#EDEDED] text-sm">{item.name}</p>
                          <p className="text-[#888] text-xs">
                            {item.size && `${item.size}`}
                            {item.size && item.color && " / "}
                            {item.color && `${item.color}`}
                            {" × "}{item.quantity}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping Info */}
                <div className="pt-4 flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <h4 className="text-[#888] label-text text-sm mb-1">Ship to:</h4>
                    <p className="text-[#EDEDED]">
                      {order.shipping_address}<br />
                      {order.shipping_city}, {order.shipping_state} {order.shipping_zip}<br />
                      <span className="text-[#888]">{order.phone}</span>
                    </p>
                  </div>

                  {/* Status Actions */}
                  <div className="flex gap-2">
                    {order.payment_status === "pending" ? (
                      <button
                        onClick={() => updateStatus(order.id, "confirmed")}
                        className="btn-skew bg-[#39FF14] text-black px-6 py-2 font-bold uppercase text-sm"
                        data-testid={`confirm-${order.id}`}
                      >
                        <span>Confirm Payment</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => updateStatus(order.id, "pending")}
                        className="btn-skew border border-[#333] text-[#888] px-6 py-2 font-bold uppercase text-sm hover:border-[#888]"
                        data-testid={`mark-pending-${order.id}`}
                      >
                        <span>Mark Pending</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
